import { Link } from "gatsby"
import React from "react"
import {
  acceptAnalytics,
  denyAnalytics,
  getAnalyticsConsent,
  loadGoogleAnalytics,
  type AnalyticsConsent,
} from "../utils/analytics"

type Props = {
  children: React.ReactNode
}

export const ConsentManager: React.FC<Props> = ({ children }) => {
  const [ready, setReady] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [consent, setConsent] =
    React.useState<AnalyticsConsent | null>(null)

  React.useEffect(() => {
    const currentConsent = getAnalyticsConsent()

    setConsent(currentConsent)
    setIsOpen(currentConsent === null)
    setReady(true)

    if (currentConsent === "granted") {
      loadGoogleAnalytics()
    }

    const openPreferences = () => {
      setConsent(getAnalyticsConsent())
      setIsOpen(true)
    }

    window.addEventListener(
      "chanoyu:open-analytics-preferences",
      openPreferences
    )

    return () => {
      window.removeEventListener(
        "chanoyu:open-analytics-preferences",
        openPreferences
      )
    }
  }, [])

  const handleAccept = () => {
    acceptAnalytics()
    setConsent("granted")
    setIsOpen(false)
  }

  const handleDeny = () => {
    denyAnalytics()
    setConsent("denied")
    setIsOpen(false)
  }

  return (
    <>
      {children}

      {ready && isOpen && (
        <aside
          className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
          role="dialog"
          aria-labelledby="analytics-consent-title"
          aria-describedby="analytics-consent-description"
        >
          <div className="space-y-4">
            <div>
              <h2
                id="analytics-consent-title"
                className="text-base font-semibold text-gray-900"
              >
                Mesure d'audience
              </h2>

              <p
                id="analytics-consent-description"
                className="mt-2 text-sm leading-6 text-gray-600"
              >
                Chanoyu utilise Google Analytics uniquement avec votre accord
                pour mesurer l'audience et améliorer le site. Refuser
                n'empêche aucune fonctionnalité.{" "}
                <Link
                  to="/politique-de-confidentialite/"
                  className="underline hover:text-gray-900"
                >
                  En savoir plus
                </Link>
                .
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              {consent !== null && (
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  Fermer
                </button>
              )}

              <button
                type="button"
                onClick={handleDeny}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Refuser
              </button>

              <button
                type="button"
                onClick={handleAccept}
                className="rounded-lg border border-gray-900 bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                Accepter
              </button>
            </div>
          </div>
        </aside>
      )}
    </>
  )
}
